import axios from '@/axios'
import { SKIP_AUTH_RECOVERY } from '@/interceptors/authResponseInterceptor'
import { isSupersededSessionError, sentBearer } from '@/router/sessionBearer'
import { isAnonymousStorefrontSessionNow } from '@/router/sessionProvenance'
import { currentStorefrontSlug, storefrontHomePath } from '@/router/storefrontSlug'
import { redactResumeCode } from '@/utils/redactResumeCode'
import { loginRedirectFor } from '@/router/postLoginRedirect'

/**
 * Should this 401 purge the session and send the user to /login?
 *
 * Not when it came from a bearer that has since been REPLACED -- purging then
 * deletes the replacement (a storefront swap or re-mint). And never for an
 * ANONYMOUS storefront visitor, who has no credentials to sign in with
 * (Jake, 2026-10-01); the auth interceptor owns their recovery, and a
 * component's catch re-raising the same 401 must not undo it.
 */
function shouldEndSessionFor401(error) {
    if (isSupersededSessionError(error)) return false
    if (
        currentStorefrontSlug() &&
        isAnonymousStorefrontSessionNow(sentBearer(error && error.config))
    ) {
        return false
    }
    return true
}

// Auto-dismiss durations (ms) by severity. Errors persist until the user
// dismisses them (they often carry an action to take); success/info are
// transient confirmations and auto-dismiss so they don't linger over the UI
// (a permanent success toast sat over the storefront cart-nav icon, a real
// papercut for shoppers -- and blocked automated cart navigation).
// `-1` = persist (Vuetify convention; App.vue binds :timeout to this).
const TIMEOUT_PERSIST = -1
// 3000ms is a conventional confirmation duration -- long enough to read a brief
// success/info message, short enough that it never lingers over the UI.
const TIMEOUT_TRANSIENT = 3000

const GENERIC_ERROR_MESSAGE =
    'An error occurred when making your request. Please refresh and try again'

// Fallback for the info/success paths when no usable message resolves.
// Deliberately NOT GENERIC_ERROR_MESSAGE: these actions announce a completed
// operation, and "An error occurred when making your request" on a green
// success toast would tell the user the opposite of what happened. Neutral
// wording that is true of every caller of raiseInfo/raiseSuccess.
const GENERIC_STATUS_MESSAGE = 'Request completed'

// A message is only usable if it is a non-empty string. Backend handlers are
// not uniform -- `error` is usually a string but some handlers pass an object,
// an array, or an exception instance -- and rendering one of those into the
// toast puts "[object Object]" in front of a user.
function usableMessage(value) {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

// Extract a human-readable message from an axios error response body.
//
// Precedence (first usable wins; the pre-existing order is preserved and
// `error` is appended, not interleaved):
//   1. `data.errors[0].detail` -- SAFRS/JSON:API. Most specific: SAFRS emits
//      per-field validation detail, so it is the best message when present.
//   2. `data.message`          -- Flask handlers using `jsonify(message=...)`.
//   3. `data.error`            -- Flask handlers using `jsonify(error=...)`.
//      Added 2026-08-12: this is the single most common error shape in the API
//      (292 `jsonify(error=...)` call sites across 22 endpoint files vs 76
//      `jsonify(message=...)`), and it was previously ignored entirely, so
//      every one of those messages fell through to the generic string.
//   4. the generic message.
//
// Each candidate is validated independently, so an unusable higher-precedence
// field (an empty `errors` array, a non-string `message`) falls through to the
// next candidate rather than throwing or rendering an object.
function extractErrorMessage(data) {
    if (!data || typeof data !== 'object') return null

    if (Array.isArray(data.errors) && data.errors.length > 0) {
        const first = data.errors[0]
        const detail = first && typeof first === 'object' ? usableMessage(first.detail) : null
        if (detail) return detail
    }

    return usableMessage(data.message) || usableMessage(data.error)
}

// Resolve the message for a non-error toast (raiseInfo / raiseSuccess) from
// whatever a caller passed. Callers hand these actions a string, a plain
// object, or -- less often -- an axios Error, and before 2026-08-12 the Error
// branch read `payload.response.data.message` unguarded, so any Error without
// a `.response` (network failure, timeout, an ordinary thrown JS error) threw
// a TypeError from inside the toast handler: the user got NO toast, and the
// original failure was masked by a second error thrown while reporting it
// (GitHub #2552, the same defect class as #2551 in raiseError).
//
// Precedence mirrors raiseError as far as it is meaningful here --
//   1. a usable string payload
//   2. `response.data.errors[0].detail` (SAFRS/JSON:API, most specific)
//   3. `response.data.message` (Flask `jsonify(message=...)`)
//   4. a plain object's `message`
//   5. GENERIC_STATUS_MESSAGE
//
// -- with one deliberate divergence: `data.error` is NOT surfaced here, which
// is why this builds its chain from usableMessage() rather than calling
// extractErrorMessage() (whose last candidate IS `data.error`). raiseError
// reads that field because it is the most common backend ERROR shape, and it
// gates the whole lookup to 4xx so operator-authored 5xx text (reports.py does
// `jsonify(error=str(e))`, which for a SQLAlchemy error carries the failing
// SQL and its bound parameters) never reaches a user. On a success/info toast
// that 4xx gate is meaningless -- these fire on 2xx -- and a body whose only
// populated field is `error` is not describing the success the caller is
// announcing. Inheriting it would risk rendering a leaked exception string in
// a green toast, so this path stops at `message`.
function statusMessageFromBody(data) {
    if (!data || typeof data !== 'object') return null

    if (Array.isArray(data.errors) && data.errors.length > 0) {
        const first = data.errors[0]
        const detail = first && typeof first === 'object' ? usableMessage(first.detail) : null
        if (detail) return detail
    }

    return usableMessage(data.message)
}

// Entry point for the info/success branch (see the precedence note above).
function resolveStatusMessage(payload) {
    if (typeof payload === 'string') {
        return usableMessage(payload) || GENERIC_STATUS_MESSAGE
    }

    if (payload instanceof Error) {
        // Optional chaining: `response` is absent on network/timeout errors and
        // on any non-axios Error, which is exactly the crash being fixed.
        //
        // Note the Error's OWN `.message` is not used as a fallback. It is
        // developer-facing text ("Network Error", "timeout of 0ms exceeded",
        // "x is not a function") authored for a stack trace, not for a user,
        // and it is not what the caller meant to announce. The generic status
        // is the honest thing to show.
        return statusMessageFromBody(payload?.response?.data) || GENERIC_STATUS_MESSAGE
    }

    // `typeof null === 'object'`, so this must tolerate null/undefined -- the
    // old object branch read `null.message` and threw.
    if (payload && typeof payload === 'object') {
        return usableMessage(payload.message) || GENERIC_STATUS_MESSAGE
    }

    return GENERIC_STATUS_MESSAGE
}

// Module-local flag: when true, raiseError silently drops the toast.
// Loaded from the platform_settings DB on boot (setSuppressErrorSnackbars action).
// Useful for demos where noisy API error toasts would disrupt a presentation.
let _suppressErrors = false

export default {
    state: {
        message: null,
        color: 'success',
        show: false,
        timer: null,
        // Severity-aware auto-dismiss duration for the active toast. Errors set
        // TIMEOUT_PERSIST; success/info set TIMEOUT_TRANSIENT. App.vue binds the
        // v-snackbar :timeout to this via the notificationTimeout getter.
        timeout: TIMEOUT_PERSIST
    },

    mutations: {
        setMessage(state, payload) {
            // Every toast passes through here: never display the storefront
            // resume code (#r=...) if a captured URL lands in a message.
            state.message = redactResumeCode(payload)
        },

        setColor(state, payload) {
            state.color = payload
        },

        setTimeout(state, payload) {
            state.timeout = payload
        },

        show(state) {
            state.show = true

            if (state.timer) {
                clearTimeout(state.timer)
                state.timer = null
            }
        },

        hide(state, timeout) {
            state.timer = setTimeout(() => {
                state.show = false
                state.message = null

                if (state.timer) {
                    clearTimeout(state.timer)
                    state.timer = null
                }
            }, timeout)
        }
    },

    actions: {
        closeErrorToaster({ commit }, timeout) {
            commit('hide', timeout)
        },

        raiseError({ commit }, payload) {
            commit('setColor', '#0C19F0')
            commit('setTimeout', TIMEOUT_PERSIST)
            let routeToLogin = false

            if (typeof payload === 'string') {
                commit('setMessage', payload)
            } else if (payload instanceof Error) {
                let errorCode
                let errorMsg

                if (payload.response && payload.response.status) {
                    errorCode = payload.response.status
                }

                errorMsg = GENERIC_ERROR_MESSAGE

                // Disclosure rule: only surface a server-supplied message for
                // 4xx. 4xx are caller-recoverable and their messages are
                // authored for the user ("email ... already exists", "slug
                // already taken") -- surfacing them is the whole point of this
                // action. 5xx messages are written for operators, and some
                // handlers return raw exception text (for example
                // api/src/endpoints/reports.py does `jsonify(error=str(e))`,
                // which for a SQLAlchemy error carries the failing SQL and its
                // bound parameters), so rendering one verbatim would leak
                // internals into a user-facing toast. 5xx therefore always gets
                // the generic message; the detail remains in the network log
                // and server logs for whoever is debugging.
                if (payload.response && errorCode >= 400 && errorCode < 500) {
                    errorMsg = extractErrorMessage(payload.response.data) || errorMsg
                }

                if (errorCode === 401) {
                    // Nothing to tell an anonymous storefront visitor, or about
                    // a superseded session: no toast, no purge, no redirect.
                    if (!shouldEndSessionFor401(payload)) return
                    errorMsg = 'Access Denied'
                    routeToLogin = true
                }

                commit('setMessage', errorMsg)
            } else if (typeof payload === 'object') {
                commit('setMessage', payload.message)
            }

            if (_suppressErrors) return
            commit('show')
            // Errors persist until dismissed (no store auto-hide timer); the
            // v-snackbar :timeout is bound to TIMEOUT_PERSIST via the getter.
            // The 401 branch below still force-navigates after its own delay.
            if (routeToLogin) {
                setTimeout(function () {
                    localStorage.removeItem('campaignId')
                    localStorage.removeItem('companyEmail')
                    localStorage.removeItem('distributorId')
                    localStorage.removeItem('firstName')
                    localStorage.removeItem('lastName')
                    localStorage.removeItem('roleId')
                    localStorage.removeItem('roleName')
                    localStorage.removeItem('selectedDepartment')
                    localStorage.removeItem('selectedManager')
                    localStorage.removeItem('selectedLocation')
                    localStorage.removeItem('token')
                    localStorage.removeItem('userId')
                    // A shopper on a storefront page goes back to THEIR STORE,
                    // never the generic /login (Jake, 2026-10-02).
                    const slug = currentStorefrontSlug()
                    // Off the storefront, keep the page they were on so signing
                    // back in returns there (plain /login when there is none).
                    const loc = window.location
                    const target = slug
                        ? storefrontHomePath(slug)
                        : loginRedirectFor(`${loc.pathname}${loc.search || ''}${loc.hash || ''}`)
                    window.location.replace(window.location.protocol + '//' + window.location.host + target)
                }, 3000)
            }
        },

        async setSuppressErrorSnackbars() {
            try {
                // A best-effort boot probe: its 401 must never end a session
                // (see SKIP_AUTH_RECOVERY in the auth interceptor).
                const resp = await axios.get('/api/platform-settings/suppress-error-snackbars', {
                    [SKIP_AUTH_RECOVERY]: true,
                })
                _suppressErrors = resp?.data?.value === true
            } catch (e) {
                // Fail open: if the setting can't be loaded, show errors normally.
                _suppressErrors = false
            }
        },

        raiseInfo({ commit }, payload) {
            commit('setColor', 'info')
            commit('setTimeout', TIMEOUT_TRANSIENT)

            commit('setMessage', resolveStatusMessage(payload))

            commit('show')
            // Transient: auto-dismiss so the confirmation does not linger over
            // the UI. Matches the :timeout the v-snackbar renders with.
            commit('hide', TIMEOUT_TRANSIENT)
        },

        raiseSuccess({ commit }, payload) {
            commit('setColor', 'success')
            commit('setTimeout', TIMEOUT_TRANSIENT)

            commit('setMessage', resolveStatusMessage(payload))

            commit('show')
            // Transient: auto-dismiss so the confirmation does not linger over
            // the UI (previously a success toast persisted ~10s over the
            // storefront cart-nav icon).
            commit('hide', TIMEOUT_TRANSIENT)
        }
    },

    getters: {
        notificationMessage(state) {
            return state.message
        },

        notificationColor(state) {
            return state.color
        },

        notificationTimeout(state) {
            return state.timeout
        },

        notificationIsActive(state) {
            return Boolean(state.show)
        }
    }
}
